import React, { useState, useEffect, useContext, useMemo } from 'react';
import EducatorLayout from './components/EducatorLayout';
import axios from '../api/api';
import { AuthContext } from '../auth/AuthContext';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Chip,
  Button,
  Stack,
  LinearProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

// Debounce helper with cancel support
function debounce(fn, wait) {
  let t;
  const debounced = (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
  debounced.cancel = () => clearTimeout(t);
  return debounced;
}

const normalizeIds = (arr) =>
  Array.isArray(arr)
    ? arr
      .map((t) => (typeof t === 'string' ? t : t?._id))
      .filter(Boolean)
    : [];

const collectSubjectNames = (student) => {
  const pools = []
    .concat(Array.isArray(student.subjects) ? student.subjects : [])
    .concat(Array.isArray(student.enrolledSubjects) ? student.enrolledSubjects : [])
    .concat(Array.isArray(student.subjectNames) ? student.subjectNames : []);

  const names = pools
    .map((entry) => (typeof entry === 'string' ? entry : entry?.name))
    .filter(Boolean);

  if (names.length > 0) return names;
  return student.subject ? [student.subject] : [];
};

const EducatorStudents = () => {
  const { token: contextToken, user } = useContext(AuthContext) ?? {};
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const navigate = useNavigate();

  const token = useMemo(() => {
    if (contextToken) return contextToken;
    if (typeof window === 'undefined') return null;
    try {
      return sessionStorage.getItem('token') || localStorage.getItem('token');
    } catch (err) {
      console.warn('Token storage unavailable', err);
      return null;
    }
  }, [contextToken]);

  useEffect(() => {
    if (!token) {
      setError('Authorization token missing');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchStudents = async () => {
      setLoading(true);
      try {
        const res = await axios.get('/users', {
          params: { role: 'student' },
          headers: { Authorization: `Bearer ${token}` },
        });

        let list = Array.isArray(res.data) ? res.data : [];

        const myId = user?._id;
        if (myId) {
          list = list.filter((s) => normalizeIds(s.tutorIds).includes(myId));
        }

        if (!cancelled) {
          setStudents(list);
          setError('');
        }
      } catch (err) {
        console.error('Error fetching students:', err);
        if (!cancelled) setError('Failed to load students');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchStudents();
    return () => {
      cancelled = true;
    };
  }, [token, user?._id]);

  const debouncedSetSearchTerm = useMemo(() => debounce(setSearchTerm, 300), []);
  useEffect(() => () => debouncedSetSearchTerm.cancel?.(), [debouncedSetSearchTerm]);

  const handleSearchChange = (event) => {
    debouncedSetSearchTerm(event.target.value);
  };

  const handleMessage = (student) => {
    if (!student?._id) {
      toast.error('Cannot open chat for this student.');
      return;
    }
    navigate(`/educator/messages?with=${encodeURIComponent(student._id)}`);
  };

  const filteredStudents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const base = students.filter((student) => {
      if (activeTab === 'active' && student.active === false) return false;
      if (activeTab === 'inactive' && student.active !== false) return false;
      return true;
    });

    if (!term) return base;

    return base.filter((student) => {
      const name = student.name?.toLowerCase() || '';
      const email = student.email?.toLowerCase() || '';
      const subjects = collectSubjectNames(student).join(' ').toLowerCase();
      return name.includes(term) || email.includes(term) || subjects.includes(term);
    });
  }, [students, searchTerm, activeTab]);

  return (
    <EducatorLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Card
          variant="outlined"
          sx={{ borderRadius: 3, boxShadow: '0 6px 14px rgba(0,0,0,0.08)' }}
        >
          <CardHeader
            title={
              <Typography variant="h4" fontWeight={800} sx={{ color: '#00154A' }}>
                Students
              </Typography>
            }
            subheader="Students assigned to you."
          />
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
            >
              <TextField
                placeholder="Search by name, email, or subject"
                onChange={handleSearchChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ minHeight: 48 }}
              >
                <Tab value="all" label={`All (${students.length})`} />
                <Tab
                  value="active"
                  label={`Active (${students.filter((s) => s.active !== false).length})`}
                />
                <Tab
                  value="inactive"
                  label={`Inactive (${students.filter((s) => s.active === false).length})`}
                />
              </Tabs>
            </Stack>
          </CardContent>
        </Card>

        <Card
          variant="outlined"
          sx={{ borderRadius: 3, boxShadow: '0 6px 14px rgba(0,0,0,0.08)' }}
        >
          <CardContent>
            {loading ? (
              <Box sx={{ py: 4 }}>
                <LinearProgress />
              </Box>
 ) : error ? (
              <Alert severity="error">{error}</Alert>
 ) : filteredStudents.length === 0 ? (
              <Typography color="text.secondary">
 No students found{searchTerm ? ' for your search.' : '.'}
              </Typography>
 ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table aria-label="Students table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Subjects</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStudents.map((student) => {
                      const subjectsDisplay = collectSubjectNames(student).join(', ') || '-';
                      const isActive = student.active ?? true;

                      return (
                        <TableRow
                          key={student._id || student.email || student.name}
                          hover
                          sx={{ '& td': { borderBottomColor: 'rgba(0,0,0,0.04)' } }}
                        >
                          <TableCell>
                            <Typography fontWeight={700} sx={{ color: '#00154A' }}>
                              {student.name || '-'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {student.email || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{subjectsDisplay}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
 label={isActive ? 'Active' : 'Inactive'}
 color={isActive ? 'success' : 'default'}
 variant={isActive ? 'filled' : 'outlined'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              variant="contained"
                              startIcon={<ChatBubbleOutlineIcon />}
                              onClick={() => handleMessage(student)}
                            >
                              Message
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </EducatorLayout>
  );
};

export default EducatorStudents;
